import React from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardIcon } from "@heroicons/react/24/outline";
import { FiChevronRight, FiSearch, FiZap } from "react-icons/fi";
import { FaLinux, FaWindows } from "react-icons/fa";

const CodeBlock = ({ code }) => {
  const [copied, setCopied] = React.useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="relative bg-gray-950 rounded-xl overflow-hidden mb-4 border border-white/10">
      <div className="flex items-center justify-between px-4 py-2 bg-white/5 border-b border-white/10">
        <div className="flex gap-1.5">
          <span className="w-3 h-3 rounded-full bg-red-500/60" />
          <span className="w-3 h-3 rounded-full bg-yellow-500/60" />
          <span className="w-3 h-3 rounded-full bg-green-500/60" />
        </div>
        <button
          onClick={copyToClipboard}
          className="flex items-center gap-1.5 px-2 py-1 rounded text-xs text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
          title="Copy to clipboard"
        >
          {copied ? (
            <span className="text-green-400 text-xs">✔ Copied</span>
          ) : (
            <>
              <ClipboardIcon className="w-3.5 h-3.5" />
              <span>Copy</span>
            </>
          )}
        </button>
      </div>
      <pre className="text-xs text-green-400 p-4 font-mono overflow-x-auto leading-relaxed max-w-full">
        {code}
      </pre>
    </div>
  );
};

const StepCard = ({ number, title, children }) => (
  <div className="relative flex gap-5 mb-8">
    <div className="flex flex-col items-center">
      <div className="w-9 h-9 shrink-0 flex items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 font-bold text-sm ring-4 ring-orange-50 dark:ring-orange-900/10">
        {number}
      </div>
    </div>
    <div className="flex-1 min-w-0 pb-2">
      <h3 className="text-base font-semibold text-text-main dark:text-white mb-3 pt-1">
        {title}
      </h3>
      {children}
    </div>
  </div>
);

const InfoBox = ({ type = "note", children }) => {
  const styles = {
    note: "bg-blue-50 dark:bg-blue-900/20 border-blue-400 text-blue-800 dark:text-blue-300",
    warning: "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-400 text-yellow-800 dark:text-yellow-300",
    tip: "bg-green-50 dark:bg-green-900/20 border-green-400 text-green-800 dark:text-green-300",
  };
  const icons = { note: "ℹ", warning: "⚠", tip: "✓" };
  return (
    <div className={`flex gap-3 border-l-4 px-4 py-3 rounded-r-lg mb-4 text-sm ${styles[type]}`}>
      <span className="font-bold mt-0.5">{icons[type]}</span>
      <p className="leading-relaxed">{children}</p>
    </div>
  );
};

const SectionHeader = ({ number, title, icon }) => (
  <div className="flex items-center gap-4 mb-6 pb-4 border-b border-border-color dark:border-border-dark">
    <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 shrink-0">
      {icon}
    </div>
    <div>
      <p className="text-xs font-bold tracking-widest text-orange-500 uppercase mb-0.5">Section {number}</p>
      <h2 className="text-xl font-bold text-text-main dark:text-white">{title}</h2>
    </div>
  </div>
);

export function renderDotnetAgent() {
  return <DotnetComponent />;
}

function DotnetComponent() {
  const navigate = useNavigate();
  return (
    <div>
      <main className="transition-opacity duration-300">
        <div className="py-6 scroll-mt-24" id="introduction">

          {/* Breadcrumbs */}
          <nav className="flex flex-wrap items-center gap-2 text-sm text-[#9a734c] mb-8">
            <a className="hover:text-primary hover:underline" href="#">Netraa APM</a>
            <FiChevronRight />
            <span>Agent Setup Guide</span>
            <FiChevronRight />
            <span>Technology Agent</span>
            <FiChevronRight />
            <span className="text-[#1b140d] dark:text-white font-medium">.NET</span>
          </nav>

          {/* Intro */}
          <div className="mb-10">
            <span className="inline-block px-3 py-1 mb-4 text-xs font-bold tracking-wider text-orange-600 uppercase bg-orange-100 rounded-full dark:bg-orange-900/30 dark:text-orange-400">
              Documentation
            </span>
            <h1 className="text-3xl lg:text-4xl font-extrabold text-text-main dark:text-white mb-4">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-red-600">
                .NET Agent Setup Guide
              </span>
            </h1>
            <p className="text-lg text-text-muted dark:text-gray-400 leading-relaxed">
              Step-by-step instructions for deploying the Apmosys APM .NET Agent in both Linux and Windows production environments.
            </p>
          </div>

          {/* Zero-Code callout */}
          <div className="flex items-start gap-4 p-5 mb-12 rounded-2xl bg-gradient-to-r from-orange-50 to-transparent dark:from-orange-900/20 dark:to-transparent border border-orange-200 dark:border-orange-800/40">
            <div className="w-10 h-10 shrink-0 flex items-center justify-center rounded-xl bg-orange-100 dark:bg-orange-900/40 text-orange-500">
              <FiZap className="w-5 h-5" />
            </div>
            <div>
              <p className="font-semibold text-text-main dark:text-white text-sm mb-1">Zero-Code Instrumentation</p>
              <p className="text-sm text-text-muted dark:text-gray-400 leading-relaxed">
                The Apmosys APM Agent uses a Zero-Code Instrumentation model — no source code modifications or NuGet packages required.
              </p>
            </div>
          </div>

          {/* Section 1: Linux */}
          <section className="mb-14 scroll-mt-24" id="linux">
            <SectionHeader number="01" title="Linux Deployment SOP" icon={<FaLinux className="w-5 h-5" />} />

            <StepCard number="1" title="Copy the Agent Files">
              <p className="text-sm text-text-muted dark:text-gray-400 mb-3">
                Transfer the <code className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-white/10 text-orange-600 dark:text-orange-400 text-xs font-mono">agent_dll</code> folder to the target Linux server. We recommend placing it in a secure, centralized location.
              </p>
              <CodeBlock code={`sudo mkdir -p /opt/apmosys\nsudo cp -r ./agent_dll /opt/apmosys/agent_dll`} />
            </StepCard>

            <StepCard number="2" title="Configure Environment Variables">
              <p className="text-sm text-text-muted dark:text-gray-400 mb-3">
                The agent is activated purely via environment variables. These must be set before the .NET application process starts.
              </p>
              <InfoBox type="note">
                If running via <strong>systemd</strong>, add these to the <code className="font-mono text-xs">[Service]</code> section of your <code className="font-mono text-xs">.service</code> file. If running manually, export them in the terminal.
              </InfoBox>
              <CodeBlock code={`# --- 1. CoreCLR Profiling Activation ---
export CORECLR_ENABLE_PROFILING=1
export CORECLR_PROFILER="{B1CD75AC-A39B-4D2A-9492-27318DF8E153}"

# --- 2. Native Profiler Path ---
# Point to the .so file matching your architecture (linux-x64 or linux-arm64)
export CORECLR_PROFILER_PATH="/opt/apmosys/agent_dll/linux-x64/ApmosysApmProfiler.so"

# --- 3. Managed Hook Path ---
export DOTNET_STARTUP_HOOKS="/opt/apmosys/agent_dll/ApmosysApm.StartupHook.dll"

# --- 4. Apmosys APM Configurations ---
export ApMosys__ServiceName="YourMicroserviceName"
export ApMosys__Transport__gRPC__Servers="http://YOUR_APMOSYS_SERVER_IP:11800"

# Optional: Set a specific instance name (e.g., Node-1). If omitted, a UUID is generated.
# export ApMosys__ServiceInstanceName="Node-1"`} />
            </StepCard>

            <StepCard number="3" title="Restart the Application">
              <p className="text-sm text-text-muted dark:text-gray-400 mb-3">
                Start or restart your .NET application. The agent will automatically attach during the boot process.
              </p>
              <CodeBlock code={`dotnet /path/to/your/app.dll`} />
            </StepCard>
          </section>

          {/* Section 2: Windows */}
          <section className="mb-14 scroll-mt-24" id="windows">
            <SectionHeader number="02" title="Windows Deployment SOP" icon={<FaWindows className="w-5 h-5" />} />

            <StepCard number="1" title="Copy the Agent Files">
              <p className="text-sm text-text-muted dark:text-gray-400">
                Transfer the <code className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-white/10 text-orange-600 dark:text-orange-400 text-xs font-mono">agent_dll</code> folder to the target Windows server. Recommended path:
              </p>
              <code className="inline-block mt-2 mb-3 px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-white/10 text-orange-600 dark:text-orange-400 text-xs font-mono">
                C:\apmosys\agent_dll
              </code>
            </StepCard>

            <StepCard number="2" title="Configure Environment Variables">
              <p className="text-sm text-text-muted dark:text-gray-400 mb-4">
                The environment variables must be exposed to the process running the .NET application.
              </p>

              {/* Sub-option A */}
              <div className="mb-5">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 dark:bg-white/10 text-xs font-semibold text-text-main dark:text-white mb-3">
                  <span className="w-4 h-4 flex items-center justify-center rounded-full bg-orange-500 text-white text-xs font-bold">A</span>
                  Console / Service Applications — Command Prompt
                </div>
                <CodeBlock code={`:: --- 1. CoreCLR Profiling Activation ---
set CORECLR_ENABLE_PROFILING=1
set CORECLR_PROFILER={B1CD75AC-A39B-4D2A-9492-27318DF8E153}

:: --- 2. Native Profiler Path ---
set CORECLR_PROFILER_PATH=C:\\apmosys\\agent_dll\\win-x64\\ApmosysApmProfiler.dll

:: --- 3. Managed Hook Path ---
set DOTNET_STARTUP_HOOKS=C:\\apmosys\\agent_dll\\ApmosysApm.StartupHook.dll

:: --- 4. Apmosys APM Configurations ---
set ApMosys__ServiceName=YourMicroserviceName
set ApMosys__ServiceInstanceName=YourInstanceName    
set ApMosys__Transport__gRPC__Servers=http://YOUR_APMOSYS_SERVER_IP:11800`} />
              </div>

              {/* Sub-option B */}
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 dark:bg-white/10 text-xs font-semibold text-text-main dark:text-white mb-3">
                  <span className="w-4 h-4 flex items-center justify-center rounded-full bg-orange-500 text-white text-xs font-bold">B</span>
                  IIS Hosted Applications — web.config
                </div>
                <InfoBox type="note">
                  For IIS-hosted apps, set variables within <code className="font-mono text-xs">web.config</code> or at the Application Pool level.
                </InfoBox>
                <CodeBlock code={`<configuration>
  <system.webServer>
    <aspNetCore processPath="dotnet" arguments=".\\MyApp.dll" stdoutLogEnabled="false" stdoutLogFile=".\\logs\\stdout">
      <environmentVariables>
        <environmentVariable name="CORECLR_ENABLE_PROFILING" value="1" />
        <environmentVariable name="CORECLR_PROFILER" value="{B1CD75AC-A39B-4D2A-9492-27318DF8E153}" />
        <environmentVariable name="CORECLR_PROFILER_PATH" value="C:\\apmosys\\agent_dll\\win-x64\\ApmosysApmProfiler.dll" />
        <environmentVariable name="DOTNET_STARTUP_HOOKS" value="C:\\apmosys\\agent_dll\\ApmosysApm.StartupHook.dll" />
        <environmentVariable name="ApMosys__ServiceName" value="YourMicroserviceName" />
        <environmentVariable name="ApMosys__ServiceInstanceName" value="YourInstanceName" />
        <environmentVariable name="ApMosys__Transport__gRPC__Servers" value="http://YOUR_APMOSYS_SERVER_IP:11800" />
      </environmentVariables>
    </aspNetCore>
  </system.webServer>
</configuration>`} />
              </div>
            </StepCard>

            <StepCard number="3" title="Restart the Application">
              <p className="text-sm text-text-muted dark:text-gray-400">
                If using Command Prompt, run <code className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-white/10 text-orange-600 dark:text-orange-400 text-xs font-mono">dotnet MyApp.dll</code>. If using IIS, restart the Application Pool and the IIS Site.
              </p>
            </StepCard>
          </section>

          {/* Section 3: Verification */}
          <section className="mb-12 scroll-mt-24" id="verification">
            <SectionHeader number="03" title="Verification & Troubleshooting" icon={<FiSearch className="w-5 h-5" />} />

            <div className="mb-6">
              <p className="text-sm text-text-muted dark:text-gray-400 mb-4">
                To verify that the agent has successfully instrumented your application, follow these steps:
              </p>
              <div className="space-y-3">
                {[
                  "Ensure the application starts without crashing.",
                  <>Check the <code className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-white/10 text-orange-600 dark:text-orange-400 text-xs font-mono">logs/</code> directory inside your application's current working directory. You should see a file named <code className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-white/10 text-orange-600 dark:text-orange-400 text-xs font-mono">apmosysapm-&#123;Date&#125;.log</code>.</>,
                  <>Look for these lines in the log file: <code className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-white/10 text-orange-600 dark:text-orange-400 text-xs font-mono">[Information] ApmosysApm.InstrumentStartup : Initializing ...</code> and <code className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-white/10 text-orange-600 dark:text-orange-400 text-xs font-mono">[Information] ApmosysApm.Transport.Grpc.ConnectionManager : Connected server[YOUR_IP:11800]</code></>,
                  "Perform a few actions or requests against your application.",
                  "Log in to your Apmosys Dashboard and verify that YourMicroserviceName appears and traces are being recorded.",
                ].map((step, i) => (
                  <div key={i} className="flex gap-3 p-4 rounded-xl border border-border-color dark:border-border-dark bg-surface-light dark:bg-surface-dark">
                    <div className="w-6 h-6 shrink-0 flex items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 font-bold text-xs mt-0.5">
                      {i + 1}
                    </div>
                    <p className="text-sm text-text-muted dark:text-gray-400 leading-relaxed">{step}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Common Issues */}
            <h3 className="text-base font-semibold text-text-main dark:text-white mb-3 mt-8">Common Issues</h3>
            <div className="space-y-3">
              <div className="p-4 rounded-xl border border-yellow-200 dark:border-yellow-800/40 bg-yellow-50 dark:bg-yellow-900/10">
                <p className="text-xs font-bold text-yellow-700 dark:text-yellow-400 uppercase tracking-wider mb-1">UnauthorizedAccess / Permission Denied</p>
                <p className="text-sm text-yellow-800 dark:text-yellow-300">
                  Ensure the user running the application (e.g., <code className="font-mono text-xs">www-data</code> or <code className="font-mono text-xs">IIS APPPOOL\DefaultAppPool</code>) has read/execute permissions on the <code className="font-mono text-xs">agent_dll</code> directory and all its contents.
                </p>
              </div>
              <div className="p-4 rounded-xl border border-yellow-200 dark:border-yellow-800/40 bg-yellow-50 dark:bg-yellow-900/10">
                <p className="text-xs font-bold text-yellow-700 dark:text-yellow-400 uppercase tracking-wider mb-1">FileNotFoundException</p>
                <p className="text-sm text-yellow-800 dark:text-yellow-300">
                  Double-check that <code className="font-mono text-xs">DOTNET_STARTUP_HOOKS</code> is an absolute path. Relative paths will fail.
                </p>
              </div>
            </div>
          </section>

{/* Visualize Data CTA */}
<section className="mb-10 flex justify-center">
  <button
    onClick={() => navigate("/homeservices")}
    className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold text-white
               bg-gradient-to-r from-orange-500 to-red-600
               hover:from-orange-600 hover:to-red-700
               shadow-md hover:shadow-lg transition-all duration-300
               dark:shadow-orange-900/30"
  >
    Visualize Data
    <FiChevronRight className="w-4 h-4" />
  </button>
</section>
        </div>
      </main>
    </div>
  );
}